import type { ModelProps, ModelRegistry } from '../types';

function CoralRed({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Base */}
      <mesh position={[0, 0.08, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.15, 0.15, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#CD5C5C' : '#8B4513'}
          roughness={0.6}
        />
      </mesh>
      {/* Branches */}
      {[0, 1, 2, 3, 4].map((i) => {
        const angle = (i * Math.PI * 2) / 5;
        return (
          <group key={i} position={[Math.cos(angle) * 0.08, 0.15, Math.sin(angle) * 0.08]} rotation={[0.3, angle, 0]}>
            <mesh position={[0, 0.15, 0]}>
              <cylinderGeometry args={[0.02, 0.04, 0.3, 6]} />
              <meshStandardMaterial
                color={world === 'real' ? '#FF6B6B' : '#CD5C5C'}
                emissive={world === 'spectral' ? '#8B0000' : undefined}
                emissiveIntensity={0.2}
                roughness={0.5}
                transparent={isOccluding}
                opacity={opacity}
              />
            </mesh>
            {/* Sub-branches */}
            {[0, 1].map((j) => (
              <mesh key={j} position={[(j - 0.5) * 0.08, 0.25, 0]} rotation={[0, 0, (j - 0.5) * 0.5]}>
                <cylinderGeometry args={[0.01, 0.02, 0.15, 4]} />
                <meshStandardMaterial color={world === 'real' ? '#FF8888' : '#E06060'} roughness={0.5} />
              </mesh>
            ))}
          </group>
        );
      })}
    </group>
  );
}

function CoralBrain({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Base */}
      <mesh position={[0, 0.08, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.15, 0.15, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#CD5C5C' : '#8B4513'}
          roughness={0.6}
        />
      </mesh>
      {/* Branches */}
      {[0, 1, 2, 3, 4].map((i) => {
        const angle = (i * Math.PI * 2) / 5;
        return (
          <group key={i} position={[Math.cos(angle) * 0.08, 0.15, Math.sin(angle) * 0.08]} rotation={[0.3, angle, 0]}>
            <mesh position={[0, 0.15, 0]}>
              <cylinderGeometry args={[0.02, 0.04, 0.3, 6]} />
              <meshStandardMaterial
                color={world === 'real' ? '#FF6B6B' : '#CD5C5C'}
                emissive={world === 'spectral' ? '#8B0000' : undefined}
                emissiveIntensity={0.2}
                roughness={0.5}
                transparent={isOccluding}
                opacity={opacity}
              />
            </mesh>
            {/* Sub-branches */}
            {[0, 1].map((j) => (
              <mesh key={j} position={[(j - 0.5) * 0.08, 0.25, 0]} rotation={[0, 0, (j - 0.5) * 0.5]}>
                <cylinderGeometry args={[0.01, 0.02, 0.15, 4]} />
                <meshStandardMaterial color={world === 'real' ? '#FF8888' : '#E06060'} roughness={0.5} />
              </mesh>
            ))}
          </group>
        );
      })}
    </group>
  );
}

function CoralBlue({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Base */}
      <mesh position={[0, 0.05, 0]}>
        <cylinderGeometry args={[0.08, 0.1, 0.1, 6]} />
        <meshStandardMaterial color={world === 'real' ? '#4169E1' : '#2a4a8a'} roughness={0.6} />
      </mesh>
      {/* Fan shape */}
      <mesh position={[0, 0.3, 0]} castShadow>
        <planeGeometry args={[0.5, 0.4]} />
        <meshStandardMaterial
          color={world === 'real' ? '#6495ED' : '#4169E1'}
          emissive={world === 'spectral' ? '#4169E1' : undefined}
          emissiveIntensity={0.3}
          side={2}
          roughness={0.4}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Fan ribs */}
      {[-0.15, 0, 0.15].map((x, i) => (
        <mesh key={i} position={[x, 0.3, 0.01]}>
          <boxGeometry args={[0.02, 0.35, 0.01]} />
          <meshStandardMaterial color={world === 'real' ? '#4682B4' : '#3a5a8a'} />
        </mesh>
      ))}
    </group>
  );
}

function CoralYellow({ world }: ModelProps) {
  return (
    <group>
      {[0, 1, 2, 3, 4, 5, 6].map((i) => {
        const angle = (i * Math.PI * 2) / 7;
        const height = 0.2 + Math.sin(i * 2) * 0.1;
        return (
          <mesh key={i} position={[Math.cos(angle) * 0.1, height / 2, Math.sin(angle) * 0.1]} castShadow>
            <cylinderGeometry args={[0.03, 0.04, height, 8, 1, true]} />
            <meshStandardMaterial
              color={world === 'real' ? '#FFD700' : '#DAA520'}
              emissive={world === 'spectral' ? '#B8860B' : undefined}
              emissiveIntensity={0.2}
              roughness={0.5}
              side={2}
            />
          </mesh>
        );
      })}
    </group>
  );
}

function Seaweed({ world }: ModelProps) {
  return (
    <group>
      {[0, 1, 2, 3].map((i) => (
        <group key={i} position={[(i - 1.5) * 0.1, 0, (i % 2) * 0.05]}>
          {[0, 1, 2, 3].map((j) => (
            <mesh key={j} position={[Math.sin(j * 0.5 + i) * 0.05, j * 0.15 + 0.1, 0]} rotation={[0, 0, Math.sin(j + i) * 0.2]}>
              <planeGeometry args={[0.08, 0.18]} />
              <meshStandardMaterial
                color={world === 'real' ? '#2E8B57' : '#1a5a3a'}
                emissive={world === 'spectral' ? '#006400' : undefined}
                emissiveIntensity={0.15}
                side={2}
                transparent
                opacity={0.9}
              />
            </mesh>
          ))}
        </group>
      ))}
    </group>
  );
}

function Anemone({ world }: ModelProps) {
  return (
    <group>
      {/* Base */}
      <mesh position={[0, 0.08, 0]} castShadow>
        <cylinderGeometry args={[0.15, 0.1, 0.15, 12]} />
        <meshStandardMaterial
          color={world === 'real' ? '#FF69B4' : '#BA55D3'}
          roughness={0.4}
        />
      </mesh>
      {/* Tentacles */}
      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => {
        const angle = (i * Math.PI * 2) / 12;
        const innerAngle = ((i + 0.5) * Math.PI * 2) / 12;
        return (
          <group key={i}>
            {/* Outer tentacles */}
            <mesh position={[Math.cos(angle) * 0.12, 0.25, Math.sin(angle) * 0.12]} rotation={[0.3, 0, Math.sin(i) * 0.2]}>
              <cylinderGeometry args={[0.015, 0.02, 0.25, 6]} />
              <meshStandardMaterial
                color={world === 'real' ? '#FF1493' : '#9932CC'}
                emissive={world === 'spectral' ? '#9400D3' : undefined}
                emissiveIntensity={0.3}
                roughness={0.3}
              />
            </mesh>
            {/* Inner shorter tentacles */}
            {i % 2 === 0 && (
              <mesh position={[Math.cos(innerAngle) * 0.06, 0.2, Math.sin(innerAngle) * 0.06]} rotation={[0.2, 0, Math.cos(i) * 0.15]}>
                <cylinderGeometry args={[0.01, 0.015, 0.15, 6]} />
                <meshStandardMaterial color={world === 'real' ? '#FFB6C1' : '#DDA0DD'} roughness={0.3} />
              </mesh>
            )}
          </group>
        );
      })}
    </group>
  );
}

function UnderwaterRock({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      <mesh position={[0, 0.15, 0]} rotation={[0.1, 0.3, 0]} castShadow>
        <dodecahedronGeometry args={[0.25, 0]} />
        <meshStandardMaterial
          color={world === 'real' ? '#696969' : '#4a4a5a'}
          roughness={0.9}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Moss/algae patches */}
      {[0, 1, 2].map((i) => (
        <mesh key={i} position={[Math.cos(i * 2) * 0.15, 0.2 + i * 0.05, Math.sin(i * 2) * 0.15]}>
          <sphereGeometry args={[0.08, 6, 6]} />
          <meshStandardMaterial
            color={world === 'real' ? '#3CB371' : '#2a6a4a'}
            roughness={0.7}
            transparent
            opacity={0.8}
          />
        </mesh>
      ))}
    </group>
  );
}

export const underwaterModels: ModelRegistry = {
  coral_red: CoralRed,
  coral_brain: CoralBrain,
  coral_blue: CoralBlue,
  coral_yellow: CoralYellow,
  seaweed: Seaweed,
  anemone: Anemone,
  underwater_rock: UnderwaterRock,
};
